import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import moment from "moment";
import { toast } from "react-toastify";

/**
 * Export data to PDF file
 * @param {Array} data - Array of objects to export
 * @param {Object} config - Configuration object
 * @param {string} config.filename - Base filename (without extension)
 * @param {Object} config.columnMapping - Object mapping data keys to display headers
 * @param {boolean} config.showToast - Whether to show success/error toast messages
 * @returns {boolean} - Success status
 */

export const exportToPDF = (data, config = {}) => {
    const {
        filename = "export",
        heading = "Report", // <-- new: heading text
        columnMapping = null,
        showToast = true,
        orientation = "portrait",
        pageSize = "a4",
    } = config;

    try {
        if (!data || !Array.isArray(data) || data.length === 0) {
            if (showToast) toast.warning("No data available to export");
            return false;
        }

        // Prepare data
        let pdfRows = [];
        let headers = [];
        if (columnMapping) {
            headers = Object.values(columnMapping);
            pdfRows = data.map((row) =>
                Object.keys(columnMapping).map((key) =>
                    row[key] !== undefined ? row[key] : ""
                )
            );
        } else {
            headers = Object.keys(data[0]);
            pdfRows = data.map((row) =>
                headers.map((key) => row[key] !== undefined ? row[key] : "")
            );
        }

        // Generate filename
        const timestamp = moment().format('lll').replace(/:/g, '-');
        const finalFilename = `${filename}_${timestamp}.pdf`;

        // Create PDF with chosen page size + orientation
        const doc = new jsPDF({
            orientation,
            unit: "mm",
            format: pageSize,
        });

        // Add heading on first page
        doc.setFontSize(16);
        doc.setFont("helvetica", "bold");
        doc.text(heading, doc.internal.pageSize.getWidth() / 2, 20, { align: "center" });

        // Add table (starting a bit lower to avoid overlapping heading)
        autoTable(doc, {
            head: [headers],
            body: pdfRows,
            startY: 30, // <-- pushes table down after heading
            didDrawPage: () => {
                // Footer with page numbers
                const pageCount = doc.internal.getNumberOfPages();
                const pageCurrent = doc.internal.getCurrentPageInfo().pageNumber;

                doc.setFontSize(10);
                doc.setFont("helvetica", "normal");
                doc.text(
                    `Page ${pageCurrent} of ${pageCount}`,
                    doc.internal.pageSize.getWidth() - 20,
                    doc.internal.pageSize.getHeight() - 10,
                    { align: "right" }
                );
            },
        });

        doc.save(finalFilename);

        if (showToast)
            toast.success(`PDF export completed successfully! File: ${finalFilename}`);
        return true;
    } catch (error) {
        console.error("PDF export error:", error);
        if (showToast)
            toast.error(`Failed to export PDF file: ${error.message}`);
        return false;
    }
};

/**
 * Export authentication history data to PDF
 * @param {Array} data - Authentication history data array
 * @param {Object} columnMapping - Column mapping for the export
 * @param {Object} customConfig - Custom configuration to override defaults
 * @returns {boolean} - Success status
 */
export const exportReportToPDF = (data, columnMapping, customConfig = {}) => {
    const defaultConfig = {
        filename: data?.name || "report_export",
        heading: data?.name || "Report",
        columnMapping,
        showToast: true,
        orientation: "landscape",
        pageSize: "a3",
    };

    const config = { ...defaultConfig, ...customConfig };
    return exportToPDF(data?.list, config);
};
